import { useCallback, useEffect, useRef, useState } from 'react';
import { MagnifyingGlass, Plus, CircleNotch, User, Buildings } from '@phosphor-icons/react';
import { searchContacts } from '@/lib/contactsApi';
import type { Contact } from '@/lib/contactsStore';

export interface PickedContact {
  /** Present when the user chose an existing contact. Empty = new. */
  contactId?: string;
  /** Display name the user ended up with (typed or picked). */
  name: string;
}

interface Props {
  label: string;
  placeholder?: string;
  value: PickedContact;
  onChange: (next: PickedContact) => void;
  /** Restrict search to a contact type (e.g. 'solicitor', 'firm'). */
  filterType?: 'lay_client' | 'solicitor' | 'firm' | 'expert' | 'witness' | 'other';
  disabled?: boolean;
}

const inputCls =
  'h-9 w-full rounded-md border border-border bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-600/40';
const labelCls = 'block text-xs font-medium text-muted-foreground mb-1.5';

export function ContactPicker({ label, placeholder, value, onChange, filterType, disabled }: Props) {
  const [query, setQuery] = useState(value.name);
  const [results, setResults] = useState<Contact[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  // Keep local input in sync when parent changes the value externally
  useEffect(() => {
    setQuery(value.name);
  }, [value.name]);

  const runSearch = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const hits = await searchContacts(q, filterType);
      setResults(hits);
    } catch (err) {
      console.error('Contact search failed:', err);
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [filterType]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, runSearch]);

  // Close dropdown on click outside
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function handleType(next: string) {
    setQuery(next);
    setOpen(true);
    // Typing invalidates any existing contactId — the user is re-authoring.
    onChange({ name: next, contactId: undefined });
  }

  function handlePick(c: Contact) {
    const picked: PickedContact = { contactId: c.id, name: c.name };
    setQuery(c.name);
    onChange(picked);
    setOpen(false);
  }

  function handleKeepAsNew() {
    // User is happy typing a brand-new name. Close the dropdown;
    // the matter service will create a Contact for this name on submit.
    setOpen(false);
  }

  const trimmed = query.trim();
  const showCreateOption = trimmed.length >= 2 && !results.some((r) => r.name.toLowerCase() === trimmed.toLowerCase());

  return (
    <div ref={containerRef} className="relative">
      <label className={labelCls}>{label}</label>
      <div className="relative">
        <MagnifyingGlass className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          className={`${inputCls} pl-8`}
          placeholder={placeholder}
          value={query}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => handleType(e.target.value)}
        />
        {searching && (
          <CircleNotch className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      {value.contactId && !open && (
        <div className="mt-1 text-[10px] text-emerald-700 dark:text-emerald-300">
          Linked to existing contact
        </div>
      )}

      {open && trimmed.length >= 2 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-border bg-card shadow-lg">
          {results.length > 0 && (
            <ul className="max-h-56 overflow-y-auto">
              {results.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                    onClick={() => handlePick(c)}
                  >
                    {c.type === 'company' || c.type === 'firm' ? (
                      <Buildings className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    )}
                    <span className="flex-1 truncate">
                      <span className="font-medium text-foreground">{c.name}</span>
                      {c.org && c.org !== '—' && (
                        <span className="ml-1.5 text-xs text-muted-foreground">· {c.org}</span>
                      )}
                    </span>
                    {c.cases > 0 && (
                      <span className="text-[10px] text-muted-foreground">{c.cases} case{c.cases > 1 ? 's' : ''}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {showCreateOption && (
            <button
              type="button"
              className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm text-brand-700 dark:text-brand-300 hover:bg-brand-50"
              onClick={handleKeepAsNew}
            >
              <Plus className="h-3.5 w-3.5" />
              <span>
                Create new contact: <span className="font-semibold">"{trimmed}"</span>
              </span>
            </button>
          )}
          {!searching && results.length === 0 && !showCreateOption && (
            <div className="px-3 py-2 text-xs text-muted-foreground">No matches.</div>
          )}
        </div>
      )}
    </div>
  );
}
